import React, { useReducer } from "react";

const sanitizeKey = (key) => {
  return key.replace(/[.#$\/\[\]]/g, "_");
};

const sanitizeObject = (obj) => {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(sanitizeObject);
  }
  return Object.keys(obj).reduce((result, key) => {
    const sanitizedKey = sanitizeKey(key);
    result[sanitizedKey] = sanitizeObject(obj[key]);
    return result;
  }, {});
};

function hasData(obj, type) {
  const baseExpectedTypes = {
    name: "string",
    llm: "string",
    type: "string",
    competitors: "object",
    brand_scores: "object",
    category_dimensions: "object",
  };

  let expectedTypes = { ...baseExpectedTypes };

  if (type === "brand") {
    expectedTypes.core_attributes = "object";
    // expectedTypes.top_products = "object";
    expectedTypes.brand_description = "string";
    expectedTypes.brand_snippets = "object";
    expectedTypes.brand_themes = "object";
    expectedTypes.entity = "string";
    expectedTypes.positive_negative_attributes = "object";
    // expectedTypes.sources=  "object";
  } else if (type === "product") {
    expectedTypes.taxonomies = "object";
    expectedTypes.brand_description = "string";
    expectedTypes.brand_snippets = "object";
    expectedTypes.brand_themes = "object";
    expectedTypes.entity = "string";
    expectedTypes.positive_negative_attributes = "object";
    // expectedTypes.sources=  "object";
  } else if (type === "category") {
    expectedTypes.taxonomies = "object";
  }

  for (const key in expectedTypes) {
    if (expectedTypes.hasOwnProperty(key)) {
      const expectedType = expectedTypes[key];
      const actualValue = obj[key];

      // Check if the property exists in the object
      if (!(key in obj)) {
        return false;
      }

      const actualType = Array.isArray(actualValue)
        ? "object"
        : typeof actualValue;

      if (actualType !== expectedType) {
        return false;
      }

      if (expectedType === "object" && Array.isArray(actualValue)) {
        if (
          actualValue.length === 0 &&
          key !== "category_dimensions" &&
          key !== "core_attributes" &&
          key !== "positive_negative_attributes" &&
          key !== "brand_themes"
        ) {
          return false;
        }
        if (key === "brand_scores") {
          if (
            type === "category"
              ? actualValue.length === 0
              : actualValue.length !== 4 ||
              actualValue.some(
                (item) =>
                  item === null ||
                  item === undefined ||
                  !item.hasOwnProperty("total") ||
                  item.hasOwnProperty("error")
              )
          ) {
            return false;
          }
        }
      } else if (expectedType === "object" && !Array.isArray(actualValue)) {
        // Check if the object is empty for specific keys
        if (
          (key === "category_dimensions" ||
            key === "core_attributes" ||
            key === "positive_negative_attributes" ||
            key === "brand_themes") &&
          Object.keys(actualValue).length === 0
        ) {
          return false;
        }
      } else if (expectedType === "string" && actualValue.trim() === "") {
        return false;
      }
    }
  }
  return true;
}

const uniqueArray = (array) => {
  return array.filter(
    (obj, index, self) => index === self.findIndex((o) => o?.name === obj?.name)
  );
};

const INITIAL_STATE = {
  name: "",
  language: window.selectedLanguage || "en",
  languageList: [{ name: "English", value: "en" }, { name: "Italian", value: "it" }],
  entity: "",
  category: "",
  categoryType: "",
  market: "",
  llm: "gpt-4o",
  type: "brand",
  loading: false,
  competitors: [],
  brand_scores: [],
  category_dimensions: {},
  core_attributes: {},
  sources: [],
  brand_description: "",
  brand_snippets: [],
  brand_themes: [],
  positive_negative_attributes: {},
  top_products: [],
  taxonomies: [],
  is_launched: false,
  abort_launch: false,
  location: "United States",
  occupation: "",
  attributes: [],
  category_taxonomies: [],
  product_prefs: [],
  pre_category_dimensions: [],
  rank_scores: [],
};

export const DiscoveryContext = React.createContext({
  location: "",
  language: "en",
  languageList: [{ name: "English", value: "en" }, { name: "Italian", value: "it" }],
  occupation: "",
  attributes: [],
  product_prefs: [],
  state: {},
  category: "",
  categoryType: "",
  market: "",
  llm: "",
  type: "",
  loading: false,
  updateId: () => { },
  updateLoading: () => { },
  brand_scores: [],
  category_taxonomies: [],
  updateCategoryTaxonomies: () => { },
  updateBrandScores: () => { },
  sources: [],
  updateSources: () => { },
  core_attributes: [],
  updateCoreAttributes: () => { },
  competitors: [],
  updatedCompetitors: () => { },
  category_dimensions: {},
  updateCategoryDimensions: () => { },
  brand_description: "",
  updateBrandDescription: () => { },
  brand_snippets: [],
  updateBrandSnippets: () => { },
  positive_negative_attributes: {},
  updatePositiveNegativeAttributes: () => { },
  top_products: [],
  updatedTopProducts: () => { },
  name: "",
  entity: "",
  pre_category_dimensions: [],
  rank_scores: [],
  updateEntity: () => { },
  updateName: () => { },
  updateType: () => { },
  is_launched: false,
  updatedLaunched: () => { },
  updateEntireState: () => { },
  resetState: () => { },
  brand_themes: [],
  updatedBrandThemes: () => { },
  updateCategory: () => { },
  updateCategoryType: () => { },
  updateMarket: () => { },
  updateLLM: () => { },
  taxonomies: {},
  updatedTaxonomies: () => { },
  abort_launch: false,
  updateAbortLaunch: () => { },
  updateLocation: () => { },
  updateOccupation: () => { },
  updateAttribute: () => { },
  updateProductPrefs: () => { },
  updatePreCategoryDimensions: () => { },
  updateRankScores: () => { },
  replaceBrandScores: () => { },
  updateLanguage: () => { }
});

function discoveryReducer(state = INITIAL_STATE, action) {
  if (action.type === "UPDATE_BRAND_SCORE") {
    const { data, index } = action.payload;
    const updatedBrandScores = [...state.brand_scores];
    const rankingScores =
      state.rank_scores.data && state.rank_scores.data.length > 0
        ? [...state.rank_scores.data]
        : [];
    const rankDataForBrandProduct = rankingScores[index];
    if (rankDataForBrandProduct && data && data?.name) {
      updatedBrandScores[index] = {
        name: data.name,
        rank: rankDataForBrandProduct.score,
        favorability: data.favorability,
        total: +rankDataForBrandProduct.score + +data.favorability,
        extras: {
          ...data.extras,
          ...state.rank_scores.extras,
          rank: [rankDataForBrandProduct],
        },
      };
    } else {
      updatedBrandScores[index] = data;
    }
    return {
      ...state,
      brand_scores: updatedBrandScores,
    };
  }
  if (action.type === "UPDATE_SOURCES") {
    const updatedSources = [...state.sources, ...action.payload];
    return {
      ...state,
      sources: updatedSources,
    };
  }
  if (action.type === "UPDATE_CORE_ATTRIBUTES") {
    return {
      ...state,
      core_attributes: action.payload,
    };
  }
  if (action.type === "UPDATE_COMPETITORS") {
    return {
      ...state,
      competitors: uniqueArray(action.payload),
    };
  }
  if (action.type === "UPDATE_CATEGORY_DIMENSIONS") {
    return {
      ...state,
      category_dimensions: action.payload,
    };
  }
  if (action.type === "UPDATE_BRAND_DESCRIPTION") {
    return {
      ...state,
      brand_description: action.payload,
    };
  }
  if (action.type === "UPDATE_BRAND_SNIPPETS") {
    return {
      ...state,
      brand_snippets: action.payload,
    };
  }
  if (action.type === "UPDATE_POSITVE_NEGATIVE_ATTRIBUTES") {
    return {
      ...state,
      positive_negative_attributes: action.payload,
    };
  }
  if (action.type === "UPDATE_TOP_PRODUCTS") {
    return {
      ...state,
      top_products: action.payload,
    };
  }
  if (action.type === "UPDATE_ENTITY") {
    return {
      // ...INITIAL_STATE,
      ...state,
      entity: action.payload,
    };
  }
  if (action.type === "UPDATE_LAUNCHED") {
    return {
      ...state,
      is_launched: action.payload,
    };
  }
  if (action.type === "UPDATE_STATE") {
    return {
      ...action.payload,
    };
  }
  if (action.type === "RESET_STATE") {
    return {
      ...INITIAL_STATE,
      type: state.type,
      language: state.language,
    };
  }
  if (action.type === "UPDATE_BRAND_THEMES") {
    return {
      ...state,
      brand_themes: action.payload,
    };
  }
  if (action.type === "UPDATE_NAME") {
    return {
      ...state,
      name: action.payload,
    };
  }
  if (action.type === "UPDATE_CATEGORY") {
    return {
      ...state,
      category: action.payload,
    };
  }

  if (action.type === "UPDATE_CATEGORY_TYPE") {
    return {
      ...state,
      categoryType: action.payload,
    };
  }
  if (action.type === "UPDATE_MARKET") {
    return {
      ...state,
      market: action.payload,
    };
  }
  if (action.type === "UPDATE_LLM") {
    return {
      ...state,
      llm: action.payload,
    };
  }
  if (action.type === "UPDATE_TYPE") {
    return {
      ...state,
      type: action.payload,
    };
  }
  if (action.type === "UPDATE_TAXONOMY") {
    return {
      ...state,
      taxonomies: action.payload,
    };
  }
  if (action.type === "UPDATE_ID") {
    return {
      ...state,
      id: action.payload,
    };
  }
  if (action.type === "UPDATE_LANGUAGE") {
    // localStorage.setItem(
    //   "language",
    //   action.payload
    // );
    window.selectedLanguage = action.payload;
    return {
      ...state,
      language: action.payload,
    };
  }
  if (action.type === "ABORT_LAUNCH") {
    return {
      ...state,
      abort_launch: action.payload,
    };
  }
  if (action.type === "UPDATE_LOCATION") {
    return {
      ...state,
      location: action.payload,
    };
  }
  if (action.type === "UPDATE_OCCUPATION") {
    return {
      ...state,
      occupation: action.payload,
    };
  }
  if (action.type === "UPDATE_ATTRIBUTE") {
    return {
      ...state,
      attributes: action.payload,
    };
  }
  if (action.type === "UPDATE_CATEGORY_TAXONOMIES") {
    return {
      ...state,
      category_taxonomies: action.payload,
    };
  }
  if (action.type === "UPDATE_PRODUCT_PREFS") {
    return {
      ...state,
      product_prefs: action.payload,
    };
  }
  if (action.type === "UPDATE_PRE_CATEGORY_DIMENSIONS") {
    return {
      ...state,
      pre_category_dimensions: action.payload,
    };
  }
  if (action.type === "UPDATE_LOADING") {
    return {
      ...state,
      loading: action.payload,
    };
  }
  if (action.type === "UPDATE_RANK_SCORES") {
    const updatedBrandScores = [...state.brand_scores];
    const { data, extras } = action.payload;
    Array.isArray(data) &&
      data.forEach((item, index) => {
        let brandIndexItem = updatedBrandScores[index];
        if (brandIndexItem && brandIndexItem.favorability) {
          updatedBrandScores[index] = {
            name: item.name,
            rank: item.score,
            favorability: brandIndexItem.favorability,
            total: +item.score + +brandIndexItem.favorability,
            extras: {
              ...extras,
              ...brandIndexItem.extras,
              rank: [item],
            },
          };
        } else {
          updatedBrandScores[index] = {
            ...updatedBrandScores[index],
            name: item.name,
            rank: item.score,
            extras: {
              ...extras,
              rank: [item],
            },
          };
        }
      });
    return {
      ...state,
      rank_scores: action.payload,
      brand_scores: updatedBrandScores,
    };
  }
  if (action.type === "REPLACE_BRAND_SCORES") {
    return {
      ...state,
      brand_scores: action.payload,
    };
  }
  return state;
}

export default function DiscoveryProvider({ children }) {
  const [state, dispatch] = useReducer(discoveryReducer, INITIAL_STATE);

  function updateLoading(payload) {
    dispatch({ type: "UPDATE_LOADING", payload });
  }

  function updateId(payload) {
    dispatch({ type: "UPDATE_ID", payload });
  }

  function updateBrandScores(payload) {
    dispatch({ type: "UPDATE_BRAND_SCORE", payload });
  }

  function updateSources(payload) {
    dispatch({ type: "UPDATE_SOURCES", payload });
  }

  function updateCoreAttributes(payload) {
    dispatch({ type: "UPDATE_CORE_ATTRIBUTES", payload });
  }

  function updatedCompetitors(payload) {
    dispatch({ type: "UPDATE_COMPETITORS", payload });
  }

  function updateCategoryDimensions(payload) {
    dispatch({ type: "UPDATE_CATEGORY_DIMENSIONS", payload });
  }

  function updateBrandDescription(payload) {
    dispatch({ type: "UPDATE_BRAND_DESCRIPTION", payload });
  }

  function updateBrandSnippets(payload) {
    dispatch({ type: "UPDATE_BRAND_SNIPPETS", payload });
  }

  function updatePositiveNegativeAttributes(payload) {
    dispatch({ type: "UPDATE_POSITVE_NEGATIVE_ATTRIBUTES", payload });
  }

  function updatedTopProducts(payload) {
    dispatch({ type: "UPDATE_TOP_PRODUCTS", payload });
  }

  function updateEntity(payload) {
    dispatch({ type: "UPDATE_ENTITY", payload });
  }

  function updatedLaunched(payload) {
    dispatch({ type: "UPDATE_LAUNCHED", payload });
  }

  function updateEntireState(payload) {
    dispatch({ type: "UPDATE_STATE", payload });
  }

  function resetState() {
    dispatch({ type: "RESET_STATE" });
  }

  function updatedBrandThemes(payload) {
    dispatch({ type: "UPDATE_BRAND_THEMES", payload });
  }

  function updateName(payload) {
    dispatch({ type: "UPDATE_NAME", payload });
  }

  function updateCategory(payload) {
    dispatch({ type: "UPDATE_CATEGORY", payload });
  }

  function updateCategoryType(payload) {
    dispatch({ type: "UPDATE_CATEGORY_TYPE", payload });
  }

  function updateMarket(payload) {
    dispatch({ type: "UPDATE_MARKET", payload });
  }

  function updateLLM(payload) {
    dispatch({ type: "UPDATE_LLM", payload });
  }

  function updateType(payload) {
    dispatch({ type: "UPDATE_TYPE", payload });
  }

  function updatedTaxonomies(payload) {
    dispatch({ type: "UPDATE_TAXONOMY", payload });
  }

  function updateLocation(payload) {
    dispatch({ type: "UPDATE_LOCATION", payload });
  }

  function updateOccupation(payload) {
    dispatch({ type: "UPDATE_OCCUPATION", payload });
  }

  function updateAttribute(payload) {
    dispatch({ type: "UPDATE_ATTRIBUTE", payload });
  }

  function updateRankScores(payload) {
    dispatch({ type: "UPDATE_RANK_SCORES", payload });
  }

  function updateAbortLaunch(payload) {
    dispatch({ type: "ABORT_LAUNCH", payload });

    setTimeout(() => {
      updateLoading(false);
      updatedLaunched(false);
      resetState();
    }, 100);
  }

  function replaceBrandScores(payload) {
    dispatch({ type: "REPLACE_BRAND_SCORES", payload });
  }

  function updateCategoryTaxonomies(payload) {
    dispatch({ type: "UPDATE_CATEGORY_TAXONOMIES", payload });
  }

  function updateProductPrefs(payload) {
    dispatch({ type: "UPDATE_PRODUCT_PREFS", payload });
  }

  function updatePreCategoryDimensions(payload) {
    dispatch({ type: "UPDATE_PRE_CATEGORY_DIMENSIONS", payload });
  }

  function updateLanguage(payload) {
    dispatch({ type: "UPDATE_LANGUAGE", payload });
  }

  return (
    <DiscoveryContext.Provider
      value={{
        state: state,
        updateId,
        location: state.location,
        attributes: state.attributes,
        occupation: state.occupation,
        loading: state.loading,
        updateLoading: updateLoading,
        rank_scores: state.rank_scores,
        product_prefs: state.product_prefs,
        brand_scores: state.brand_scores,
        sources: state.sources,
        core_attributes: state.core_attributes,
        competitors: state.competitors,
        category_dimensions: state.category_dimensions,
        brand_description: state.brand_description,
        brand_snippets: state.brand_snippets,
        brand_themes: state.brand_themes,
        positive_negative_attributes: state.positive_negative_attributes,
        top_products: state.top_products,
        entity: state.entity,
        name: state.name,
        category: state.category,
        category: state.categoryType,
        market: state.market,
        llm: state.llm,
        is_launched: state.is_launched,
        taxonomies: state.taxonomies,
        type: state.type,
        pre_category_dimensions: state.pre_category_dimensions,
        abort_launch: state.abort_launch,
        category_taxonomies: state.category_taxonomies,
        language: window.selectedLanguage || state.language,
        languageList: state.languageList,
        updateBrandScores,
        updateSources,
        updateCoreAttributes,
        updatedCompetitors,
        updateCategoryDimensions,
        updateBrandDescription,
        updateBrandSnippets,
        updatePositiveNegativeAttributes,
        updatedTopProducts,
        updateEntity,
        updateName,
        updatedLaunched,
        updateEntireState,
        resetState,
        updatedBrandThemes,
        updateCategory,
        updateCategoryType,
        updateMarket,
        updateLLM,
        updateType,
        updatedTaxonomies,
        updateAbortLaunch,
        updateAttribute,
        updateLocation,
        updateOccupation,
        updateCategoryTaxonomies,
        updateProductPrefs,
        updatePreCategoryDimensions,
        updateRankScores,
        replaceBrandScores,
        updateLanguage
      }}
    >
      {children}
    </DiscoveryContext.Provider>
  );
}
